/* The console's design tokens, lifted verbatim from the Admin design canvas.
 *
 * This is the ONLY place a raw hex belongs. The Mantine theme in ./theme.ts
 * repeats a value only where Mantine needs it as a colour tuple or a CSS
 * variable; everything else reads it from here. */

export const tokens = {
  // Surfaces, lightest-used to darkest.
  card: '#FFFDF9', // raised surfaces, inputs
  sidebar: '#F7F1E7', // navbar, and the row-hover / selected-row tint
  rail: '#EFE8DC', // chip-rail track, table header band, note blocks
  page: '#EDE4D5', // app background, and every meter track
  navActive: '#EDE2CF', // active nav item, active menu row, scoped banner

  // Lines. Three weights, and they are genuinely different.
  cardBorder: '#E9E0D1', // card outline
  border: '#E4DACB', // input / chip-rail / button outline
  rowLine: '#F2ECE1', // table row divider
  lineSoft: '#F0E8DB', // in-card divider
  lineHard: '#E0D3BE', // section divider, tab strip

  // Ink.
  ink: '#2E2A26',
  ink2: '#4A423A', // table cell text
  ink3: '#6B6157', // body copy
  ink4: '#8A7C6C', // captions, inactive labels
  ink5: '#9C8F7E', // column labels, mono ids
  muted: '#A79A88', // placeholders
  faint: '#C9BCA9', // disabled controls, avatar

  // Brand.
  coral: '#F2603F',
  coralHover: '#D74B2B',
  coralDeep: '#B04A2E',
  coralSoft: '#FBE3DC',
  coralBorder: '#F0A48E',
  danger: '#C4523A',
  dangerSoft: '#FBE3DC',
  dangerBorder: '#F2C6B8',

  teal: '#12A594',
  tealSoft: '#D6EFE8',
  tealFg: '#17796B',

  marigold: '#E8B33C',
  marigoldSoft: '#F6E3B8',
  marigoldSoft2: '#F0E2C6',
  marigoldFg: '#8A6A3A',
  marigoldDeep: '#C4622A',
  barWarn: '#D9902E',

  violet: '#7C5CD6',
  moonshotSoft: '#EDDCF3',
  moonshotFg: '#7B4E96',

  okSoft: '#DDEEE2',
  okFg: '#2C7A55',
  okCard: '#F4F8F2',

  periwinkle: '#4C57A8',
  periwinkleSoft: '#DFE2F6',

  deepseekSoft: '#DCE8F6',
  deepseekFg: '#3A6699',

  neutralSoft: '#E6E2DC',
  neutralFg: '#5A5148',

  toggleOff: '#D8CEBE',

  fontMono: "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  fontUI: 'Figtree, system-ui, -apple-system, sans-serif',
} as const;

/** A pill is a [background, foreground] pair. Every badge in the console picks
 *  one of these; the design's five parallel colour maps (providers, estates,
 *  statuses, ledger statuses, message roles) collapse to exactly this. */
export type Tone =
  | 'ok'
  | 'info'
  | 'warn'
  | 'fail'
  | 'violet'
  | 'blue'
  | 'neutral'
  | 'coral';

export const toneTint: Record<Tone, readonly [string, string]> = {
  ok: [tokens.okSoft, tokens.okFg],
  info: [tokens.tealSoft, tokens.tealFg],
  warn: [tokens.marigoldSoft2, tokens.marigoldFg],
  fail: [tokens.dangerSoft, tokens.coralDeep],
  violet: [tokens.moonshotSoft, tokens.moonshotFg],
  blue: [tokens.periwinkleSoft, tokens.periwinkle],
  neutral: [tokens.neutralSoft, tokens.neutralFg],
  coral: [tokens.coralSoft, tokens.coralDeep],
};

/** Providers keep hand-picked pairs rather than a tone, because two of them
 *  (deepseek, gemini) do not sit on any tone in the map. */
const PROVIDER_TINT: Record<string, readonly [string, string]> = {
  anthropic: [tokens.marigoldSoft2, tokens.marigoldFg],
  openai: [tokens.tealSoft, tokens.tealFg],
  meta: [tokens.periwinkleSoft, tokens.periwinkle],
  moonshot: [tokens.moonshotSoft, tokens.moonshotFg],
  deepseek: [tokens.deepseekSoft, tokens.deepseekFg],
  xai: [tokens.neutralSoft, tokens.neutralFg],
  gemini: [tokens.marigoldSoft, tokens.marigoldFg],
};

export function providerTint(provider: string): readonly [string, string] {
  return PROVIDER_TINT[provider.toLowerCase()] ?? toneTint.neutral;
}

/** Estates are coloured so a row's owner is readable without reading it. New
 *  estates fall through to neutral rather than colliding with an existing one. */
const ESTATE_TINT: Record<string, readonly [string, string]> = {
  sw: [tokens.marigoldSoft2, tokens.marigoldFg],
  am: [tokens.tealSoft, tokens.tealFg],
  hal: [tokens.neutralSoft, tokens.neutralFg],
};

export function estateTint(estate: string): readonly [string, string] {
  return ESTATE_TINT[estate.toLowerCase()] ?? toneTint.neutral;
}

/** Status string -> tone. Covers pool-key states, estate/tenant states and the
 *  ledger's per-row outcome, which is why it is one function and not three. */
export function statusTone(status: string): Tone {
  switch (status.toLowerCase()) {
    case 'active':
    case 'enabled':
    case 'ok':
    case 'matched':
      return 'ok';
    case 'rested':
    case 'refused':
    case 'attributed only':
      return 'warn';
    case 'quota paused':
    case 'exhausted':
    case 'conflict':
    case 'error':
    case 'provider_error':
    case 'rate_limited':
    case 'provider_unavailable':
    case 'internal':
    case 'invalid_request':
    case 'quota_exceeded':
      return 'fail';
    case 'disabled':
    case 'canceled':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/** A quota meter earns its colour from the number, not from a status field:
 *  green while there is room, amber approaching, red at the wall. */
export function quotaColor(percent: number): string {
  if (percent >= 90) return tokens.danger;
  if (percent >= 60) return tokens.barWarn;
  return tokens.teal;
}
